import querystring from 'querystring'

import PropTypes from 'prop-types'
import React, { useCallback, useState } from 'react'
import config from 'config'
import { useForm } from 'react-hook-form'

import { required } from '@cred/shared/validations'

import { minMaxLoanAmount } from 'student-loan-refi/rate-table/validations'
import LendersList from 'components/lenders-list'
import StudentLoanRefiFilterForm from 'components/filter-form'
import Pagination from 'components/styled-pagination'
import withRateScript from 'components/hocs/with-rate-script'
import { STUDENT_LOAN_REFI_FILTER_FORM_INITIAL_VALUES as initialValues } from 'common/constants'
import { loadLendersRates, filterLender } from 'common/utils/rates'
import useActiveLenders from 'components/hooks/useActiveLenders'

import {
  generateFilterText,
  getCreditScore,
  getLoanAmount,
  getPageStats,
  getPartnerDefaultFilterValues,
} from '../utils'
import FilterModal from '../components/filter-modal'

const paginationName = 'refi-rate-table'
const LENDER_PER_PAGE = 3

const getCtaUrl = (utms, omitUtmParams, metaQuery) => {
  const params = {
    utm_source: utms.utm_source,
    utm_medium: utms.utm_medium,
    utm_campaign: utms.utm_campaign,
    utm_content: utms.utm_content,
    credclid: utms.credclid,
    renderId: utms.renderId,
  }

  if (omitUtmParams) {
    return `${config.credibleRoot}/refinance/prequal/signup${metaQuery}`
  }

  return `${config.credibleRoot}/refinance/prequal/signup?${querystring.encode(params)}${metaQuery}`
}

/* Get lenders rates from Rates library. */
const getLenders = (availableLenders, props, formFilterValues) => {
  const { isScriptLoaded } = props
  const creditScore = getCreditScore(formFilterValues.creditScoreApprox)
  const loanAmount = getLoanAmount(formFilterValues.loanAmount)

  /* Get the rates after loading the ratesLibrary JS and do it only once. */
  if (isScriptLoaded) {
    return loadLendersRates({
      availableLenders: filterLender(availableLenders, creditScore, loanAmount),
      filters: { vertical: 'refi' },
    })
  }

  return []
}

const StudentLoanRefiRateTable = ({
  utms,
  variation,
  initialize,
  widgetContext,
  omitUtmParams = false,
  metaQuery,
  theme,
  ...props
}) => {
  const defaultValues = {
    ...initialValues,
    ...getPartnerDefaultFilterValues(widgetContext, 'student-loan-refi'),
  }
  const { control, formState, reset, getValues } = useForm({
    defaultValues,
    mode: 'onChange',
  })
  const [page, setPage] = useState(0)

  const ctaUrl = getCtaUrl(utms, omitUtmParams, metaQuery)
  const [filterValues, setFilterValues] = useState(defaultValues)
  const availableLenders = useActiveLenders('refi')

  const lenders = getLenders(availableLenders, props, filterValues)
  const { pageCount, pageFrom, pageTo, showPagination } = getPageStats(
    variation,
    lenders,
    page,
    LENDER_PER_PAGE,
  )

  const minMaxLoanAmountValidator = useCallback(
    (value) => {
      return minMaxLoanAmount(availableLenders)(value)
    },
    [availableLenders],
  )

  const cancel = (closeModal) => {
    closeModal()
    // Setting timeout so the form doesn't reset as the modal is disappearing
    setTimeout(() => {
      reset(filterValues)
    }, 500)
  }

  const onUpdate = (closeModal) => {
    const { creditScoreApprox, loanAmount } = getValues()
    setFilterValues({ creditScoreApprox, loanAmount })
    closeModal()
  }

  const onPageChange = (_, selectedPage) => {
    setPage(selectedPage)
  }

  const onReset = () => {
    reset(defaultValues)
    setFilterValues(defaultValues)
  }

  return (
    <div>
      <FilterModal formName="refi-filter-form" filterText={generateFilterText(filterValues)}>
        {({ closeModal }) => (
          <StudentLoanRefiFilterForm
            withFooter
            customValidation={[required, minMaxLoanAmountValidator]}
            update={() => onUpdate(closeModal)}
            cancel={() => cancel(closeModal)}
            control={control}
            invalid={formState.isDirty && !formState.isValid}
          />
        )}
      </FilterModal>
      <LendersList
        theme={theme}
        lenders={lenders.slice(pageFrom, pageTo)}
        ctaUrl={ctaUrl}
        name="multiVerticalRefi"
        resetFilters={onReset}
      />
      {showPagination && (
        <Pagination onChange={onPageChange} pageCount={pageCount} name={paginationName} />
      )}
    </div>
  )
}

StudentLoanRefiRateTable.propTypes = {
  page: PropTypes.number,
  variation: PropTypes.string,
  initialize: PropTypes.func,
  utms: PropTypes.shape({
    utm_source: PropTypes.string,
    utm_campaign: PropTypes.string,
    utm_medium: PropTypes.string,
    utm_content: PropTypes.string,
    credclid: PropTypes.string,
    renderId: PropTypes.string,
  }),
  omitUtmParams: PropTypes.bool,
  widgetContext: PropTypes.object,
  metaQuery: PropTypes.string,
  theme: PropTypes.string.isRequired,
}

export { StudentLoanRefiRateTable as StudentLoanRefiRateTableNoHoc, getCtaUrl }

export default withRateScript(StudentLoanRefiRateTable)
